import React from "react";
import { isEmpty, noop, partial } from "lodash";
import { useFormik } from "formik";
import { Header, Icon, Radio } from "semantic-ui-react";
import Tooltip from "../../../../../../components/common/tooltip/Tooltip";
import ExpandableLinks from "../../../../../../components/ExpandableItems/ExpandableLinks/ExpandableLinks";
import validationSchemas from "../../../../../../utils/validationSchemas";
import { ValidatedForm } from "../../../../../../components/forms";
import { type NormalizedDropdown } from "utils/miscellaneousUtils";
import { getReceiveUpdatesModalData, type SettingsPayload } from "./helper";
import { TagsEnum } from "interfaces/TagsEnum";
import { renderCustomLabel } from "components/multiSelect/helpers";
import styles from "./SettingsForm.module.scss";
import { ReceiveUpdatesTypes } from "../../../types/flowBase";

export type Option = { text: string; value: string };
export type TagOptions = {
  labels?: Option[];
  softwareApplications?: Option[];
};

export interface SettingsFormProps {
  settingsInfo: SettingsPayload;
  isDisabled: boolean;
  isLoading?: boolean;
  options: TagOptions;
  changeReceiveUpdatesType: (flowId: number, type: ReceiveUpdatesTypes) => void;
  updateTags: (flowId: number, selectedTags: string[], field: keyof TagOptions) => void;
  setNewFlowTag: (payload: { type: TagsEnum; value: string }) => void;
}

export const SettingsForm = (props: SettingsFormProps) => {
  const { settingsInfo, isLoading } = props;
  const { allUsersReceiveUpdatesTooltip, usersWhoHaveNotStartedReceiveUpdatesTooltip } = getReceiveUpdatesModalData();

  const tagsFormik = useFormik({
    enableReinitialize: true,
    initialValues: {
      labels: settingsInfo.labels,
      softwareApplications: settingsInfo.softwareApplications,
    },
    validationSchema: validationSchemas.flowSettings,
    onSubmit: noop,
  });

  const handleChangeTag = async (field: keyof TagOptions, value: NormalizedDropdown, shouldValidate?: boolean) => {
    const { updateTags } = props;
    const validationErrors = await tagsFormik.setFieldValue(field, value.selected, shouldValidate);
    if (isEmpty(validationErrors?.[field])) {
      updateTags(settingsInfo.id, value.selected, field);
    }
  };

  const onAddTag = (type: TagsEnum, _event: React.ChangeEvent<HTMLInputElement>, { value }: { value: string }) => {
    const { setNewFlowTag } = props;
    setNewFlowTag({ type, value });
  };

  const handleReceiveUpdatesTypeChange = (type: ReceiveUpdatesTypes) => {
    const { changeReceiveUpdatesType, settingsInfo } = props;
    if (settingsInfo.updateType === type) {
      return;
    }
    changeReceiveUpdatesType(settingsInfo.id, type);
  };

  const renderFields = () => {
    const { options, isDisabled } = props;

    const renderReceiveUpdatesSection = () => {
      return (
        <>
          <Header>Who should receive flow updates?</Header>
          <div className={styles["receive-updates-options"]}>
            <div className={styles["receive-updates-options-item"]}>
              <Radio
                id="allUsersRadio"
                className={styles["receive-updates-toggle"]}
                label="All users"
                checked={settingsInfo.updateType === ReceiveUpdatesTypes.All}
                onClick={() => handleReceiveUpdatesTypeChange(ReceiveUpdatesTypes.All)}
              />
              <Tooltip
                tooltipClassName={styles["receive-updates-tooltip-item"]}
                target={<Icon className="info circle" />}
                content={allUsersReceiveUpdatesTooltip}
                position="top center"
                hideOnScroll
                showAlways
              />
            </div>
            <div className={styles["receive-updates-options-item"]}>
              <Radio
                id="usersWhoHaveNotStartedRadio"
                className={styles["receive-updates-toggle"]}
                label="Users who haven't started"
                checked={settingsInfo.updateType === ReceiveUpdatesTypes.NotStarted}
                onClick={() => handleReceiveUpdatesTypeChange(ReceiveUpdatesTypes.NotStarted)}
              />
              <Tooltip
                tooltipClassName={styles["receive-updates-tooltip"]}
                target={<Icon className="info circle" />}
                content={usersWhoHaveNotStartedReceiveUpdatesTooltip}
                position="top center"
                hideOnScroll
                showAlways
              />
            </div>
          </div>
        </>
      );
    };

    return (
      <div className={styles.root}>
        {!isLoading && renderReceiveUpdatesSection()}
        <Header>Labeling</Header>
        <div className={styles["settings-fields"]}>
          <ValidatedForm.MultiSelectWithAdditionField
            {...tagsFormik}
            options={options?.softwareApplications}
            label="Software Application"
            value={tagsFormik.values.softwareApplications}
            propertyName="softwareApplications"
            disabled={isDisabled}
            shouldValidate
            isCaseSensitive
            setFieldValue={handleChangeTag}
            onAddItem={partial(onAddTag, TagsEnum.SoftwareApplication)}
            renderLabel={renderCustomLabel}
          />
          <ValidatedForm.MultiSelectWithAdditionField
            {...tagsFormik}
            options={options?.labels}
            label="Labels"
            value={tagsFormik.values.labels}
            propertyName="labels"
            disabled={isDisabled}
            shouldValidate
            isCaseSensitive
            setFieldValue={handleChangeTag}
            onAddItem={partial(onAddTag, TagsEnum.Label)}
            renderLabel={renderCustomLabel}
          />
          <ExpandableLinks
            label="Associated Packs"
            linkFormatter={(linkId) => `/licensing/packs/listings/${linkId}`}
            iconName="fa-box-full"
            links={settingsInfo.packs}
          />
        </div>
      </div>
    );
  };

  return (
    <ValidatedForm
      disablePreventTransitionPrompt
      dirty={tagsFormik.dirty}
      isLoading={isLoading}
      formWidthComputer={12}
      withoutFormik
    >
      {renderFields}
    </ValidatedForm>
  );
};

export default SettingsForm;
